/* Scandinavian Edition — Performance explorer */
(() => {
  if (customElements.get('se-performance')) return;

  const signed = (v) => (v < 0 ? '−' : v > 0 ? '+' : '') + Math.abs(v) + '°';
  const plain = (v) => (v < 0 ? '−' : '') + Math.abs(v) + '°';

  class SePerformance extends HTMLElement {
    connectedCallback() {
      const configEl = this.querySelector('[data-se-config]');
      if (!configEl) return;
      try {
        this.cfg = JSON.parse(configEl.textContent);
      } catch (e) {
        return;
      }

      this.range = this.querySelector('[data-se-range]');
      this.presets = Array.from(this.querySelectorAll('[data-se-preset]'));
      this.shift = 0;
      this.t = Number(this.range?.value ?? 0);
      this.condition = this.querySelector('[data-se-condition]')?.textContent || '';
      this.#initColdToggle();

      this.range?.addEventListener('input', () => {
        this.#select(null);
        this.#update(Number(this.range.value), this.cfg.customLabel);
      });
      this.#initTouch();
      this.#initTiles();

      /* A preset that starts out too cold / too warm gets its suggestion right away */
      if (this.range) {
        const t0 = Number(this.range.value);
        const { min, max, layerMin } = this.#limits();
        const k0 = t0 < layerMin ? 'cold' : t0 < min ? 'layer' : t0 <= max ? 'ok' : 'warm';
        this.#suggest(t0, k0);
      }

      this.presets.forEach((btn) => {
        btn.addEventListener('click', () => {
          const t = Number(btn.dataset.temp);
          this.#select(btn);
          if (this.range) this.range.value = String(t);
          this.#update(t, btn.dataset.condition);
        });
      });
    }

    /* Mobile: the stat tiles open the details drawer at the matching row */
    #initTiles() {
      const mobile = window.matchMedia('(max-width: 749px)');
      const button = this.querySelector('.se-perf__details-button');
      if (!button) return;
      const open = (row) => {
        button.click();
        const target = this.querySelector(`[data-se-row="${row}"]`);
        if (!target) return;
        /* Wait for the drawer to open before scrolling inside it */
        setTimeout(() => target.scrollIntoView({ block: 'start', behavior: 'smooth' }), 260);
      };
      this.querySelectorAll('[data-se-open]').forEach((tile) => {
        const setA11y = () => {
          if (mobile.matches) {
            tile.setAttribute('role', 'button');
            tile.setAttribute('tabindex', '0');
          } else {
            tile.removeAttribute('role');
            tile.removeAttribute('tabindex');
          }
        };
        setA11y();
        mobile.addEventListener?.('change', setA11y);
        tile.addEventListener('click', () => {
          if (mobile.matches) open(tile.dataset.seOpen);
        });
        tile.addEventListener('keydown', (e) => {
          if (!mobile.matches || (e.key !== 'Enter' && e.key !== ' ')) return;
          e.preventDefault();
          open(tile.dataset.seOpen);
        });
      });
    }

    /* Touch: tap anywhere on the scale to jump there, and drag from anywhere – not just the thin handle */
    #initTouch() {
      const r = this.range;
      if (!r) return;
      const setFromX = (x) => {
        const box = r.getBoundingClientRect();
        const lo = Number(r.min);
        const hi = Number(r.max);
        const half = 16; // half the thumb width in se-performance.css, so the handle lands under the finger
        const p = Math.max(0, Math.min(1, (x - box.left - half) / (box.width - half * 2)));
        const v = String(Math.round(lo + p * (hi - lo)));
        if (v !== r.value) {
          r.value = v;
          r.dispatchEvent(new Event('input', { bubbles: true }));
        }
      };
      r.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'touch') return;
        e.preventDefault();
        r.setPointerCapture?.(e.pointerId);
        this.dragging = e.pointerId;
        setFromX(e.clientX);
      });
      r.addEventListener('pointermove', (e) => {
        if (this.dragging !== e.pointerId) return;
        setFromX(e.clientX);
      });
      const end = (e) => {
        if (this.dragging === e.pointerId) this.dragging = null;
      };
      r.addEventListener('pointerup', end);
      r.addEventListener('pointercancel', end);
    }

    #select(active) {
      this.presets.forEach((b) => b.setAttribute('aria-pressed', b === active ? 'true' : 'false'));
    }

    /* "I tend to feel the cold": moves this jacket's range warmer by coldShift degrees */
    #initColdToggle() {
      const box = this.querySelector('[data-se-cold]');
      if (!box) return;
      const note = this.querySelector('[data-se-cold-note]');
      const apply = (on, save) => {
        box.checked = on;
        this.shift = on ? Number(this.cfg.coldShift) || 0 : 0;
        if (note) note.hidden = !on;
        if (save) {
          try {
            localStorage.setItem('se-runs-cold', on ? '1' : '0');
          } catch (e) {}
        }
        this.#zones();
        this.#update(this.t, this.condition);
      };
      box.addEventListener('change', () => apply(box.checked, true));
      let stored = false;
      try {
        stored = localStorage.getItem('se-runs-cold') === '1';
      } catch (e) {}
      if (stored) apply(true, false);
    }

    #limits() {
      const { min, max, layerMin } = this.cfg;
      return { min: min + this.shift, max: max + this.shift, layerMin: layerMin + this.shift };
    }

    #zones() {
      const { scaleMin, scaleMax } = this.cfg;
      if (scaleMin == null || scaleMax == null) return;
      const span = scaleMax - scaleMin || 1;
      const pct = (v) => Math.max(0, Math.min(100, ((v - scaleMin) * 100) / span));
      const { min, max, layerMin } = this.#limits();
      const layerZone = this.querySelector('.se-perf__zone--layer');
      const okZone = this.querySelector('.se-perf__zone--ok');
      if (layerZone) {
        layerZone.style.left = pct(layerMin) + '%';
        layerZone.style.width = pct(min) - pct(layerMin) + '%';
      }
      if (okZone) {
        okZone.style.left = pct(min) + '%';
        okZone.style.width = pct(max) - pct(min) + '%';
      }
    }

    #fill(text) {
      const { min, max, layerMin } = this.#limits();
      return String(text || '')
        .split('[min]').join(signed(min))
        .split('[max]').join(signed(max))
        .split('[layer]').join(signed(layerMin));
    }

    #update(t, condition) {
      this.t = t;
      this.condition = condition;
      const { verdicts } = this.cfg;
      const { min, max, layerMin } = this.#limits();
      let key = 'warm';
      if (t < layerMin) key = 'cold';
      else if (t < min) key = 'layer';
      else if (t <= max) key = 'ok';

      const [title, text] = verdicts[key];
      const big = plain(t);

      this.querySelectorAll('[data-se-big]').forEach((el) => (el.textContent = big));
      this.querySelectorAll('[data-se-condition]').forEach((el) => (el.textContent = condition || ''));
      const titleEl = this.querySelector('[data-se-title]');
      const textEl = this.querySelector('[data-se-text]');
      if (titleEl) titleEl.textContent = title;
      if (textEl) textEl.textContent = this.#fill(text);
      this.range?.setAttribute('aria-valuetext', big + ', ' + title);
      this.#suggest(t, key);
    }

    /* ---------- "Consider X instead" when it is too cold / too warm for this jacket ---------- */
    async #candidates() {
      if (!this.candidatesPromise) {
        const url = this.cfg.suggest?.url;
        this.candidatesPromise = url
          ? fetch(url)
              .then((r) => (r.ok ? r.text() : ''))
              .then((html) => {
                const doc = new DOMParser().parseFromString(html, 'text/html');
                return JSON.parse(doc.querySelector('[data-se-candidates]')?.textContent || '[]');
              })
              .catch(() => [])
          : Promise.resolve([]);
      }
      return this.candidatesPromise;
    }

    async #suggest(t, key) {
      const links = Array.from(this.querySelectorAll('[data-se-suggest]'));
      const s = this.cfg.suggest;
      if (!links.length || !s?.enabled) return;
      const token = (this.suggestToken = (this.suggestToken || 0) + 1);
      const hideAll = () => links.forEach((l) => (l.hidden = true));
      if (key === 'ok') return hideAll();
      const all = await this.#candidates();
      if (token !== this.suggestToken) return; // the customer has moved on

      const kind = (type) => {
        const v = String(type || '').toLowerCase();
        if (v === 'west' || v.includes('vest') || v.includes('gilet')) return 'vest';
        if (v.includes('short')) return 'shorts';
        if (v.includes('jacket') || v.includes('coat') || v.includes('parka')) return 'jacket';
        return 'other';
      };
      const mine = kind(s.type);
      const myGenders = (s.gender || '').split(',').filter(Boolean);
      const pool = all.filter(
        (c) =>
          c.available &&
          c.model !== s.model &&
          (!myGenders.length || !c.gender || c.gender.split(',').some((g) => myGenders.includes(g)))
      );
      const jackets = pool.filter((c) => kind(c.type) === 'jacket');
      const vests = pool.filter((c) => kind(c.type) === 'vest');
      /* Bulky vests (e.g. Vesty) are worn on top, never underneath. Thin shells (e.g. Link, Veil II)
         get them suggested as a layer on top instead. Both lists come from the section settings. */
      const names = (v) => String(v || '').toLowerCase().split(',').map((x) => x.trim()).filter(Boolean);
      const base = (m) => String(m || '').toLowerCase().trim();
      const overVests = names(s.overVests);
      const isOverVest = (c) => overVests.some((n) => base(c.model) === n || base(c.model).startsWith(n + ' '));
      const layerVests = vests.filter((c) => !isOverVest(c));
      const topVests = vests.filter(isOverVest);
      const wearsOnTop = mine === 'jacket' && names(s.overModels).includes(base(s.model));
      const layerPick = () => {
        const pool2 = wearsOnTop && topVests.length ? topVests : layerVests;
        const v = pool2.slice().sort((a, b) => a.min - b.min)[0];
        return v ? [v, wearsOnTop && topVests.includes(v) ? labels.over : labels.layer] : null;
      };
      const layer = Number(s.layer) || 0;
      const labels = s.labels || {};
      /* For someone who runs cold, other products need the same margin */
      t = t - this.shift;
      const covers = (c) => c.min <= t && t <= c.max;
      const lightest = (a, b) => a.max - b.max || b.min - a.min;
      const warmestFirst = (a, b) => b.min - a.min || a.max - b.max; // the lightest that is still warm enough

      const picks = [];
      if (key === 'warm') {
        /* Too warm: a lighter jacket that copes, and a vest for those who want less */
        const j = jackets.filter(covers).sort(lightest)[0];
        if (j) picks.push([j, labels.lighter]);
        const v = vests.filter(covers).sort(lightest)[0];
        if (v && mine !== 'vest') picks.push([v, labels.vest]);
      } else if (key === 'layer') {
        /* Works with a layer: suggest the layer, and a jacket that manages on its own */
        if (mine !== 'vest') {
          const lp = layerPick();
          if (lp) picks.push(lp);
        }
        const j = jackets.filter(covers).sort(warmestFirst)[0];
        if (j) picks.push([j, labels.warmer]);
      } else {
        /* Too cold even with a layer: a warmer jacket, ideally one that copes on its own */
        let j = jackets.filter(covers).sort(warmestFirst)[0];
        let needsLayer = false;
        if (!j) {
          j = jackets.filter((c) => c.min - layer <= t && t <= c.max).sort(warmestFirst)[0];
          needsLayer = !!j;
        }
        if (j) picks.push([j, labels.warmer]);
        if (needsLayer) {
          const lp = layerPick();
          if (lp) picks.push(lp);
        }
      }

      links.forEach((link, i) => {
        const entry = picks[i];
        if (!entry) {
          link.hidden = true;
          return;
        }
        const [first, label] = entry;
        /* Same colour as this product when the suggested model has it */
        const sameModel = pool.filter((c) => c.model === first.model && c.min === first.min && c.max === first.max);
        const pick = sameModel.find((c) => c.color && c.color === s.color) || first;
        link.href = pick.url;
        link.querySelector('[data-se-suggest-kicker]').textContent = label || labels.jacket || '';
        link.querySelector('[data-se-suggest-title]').textContent = pick.model;
        link.querySelector('[data-se-suggest-range]').textContent = `${signed(pick.min)} – ${signed(pick.max)}`;
        const media = link.querySelector('[data-se-suggest-media]');
        media.innerHTML = '';
        if (pick.image) {
          const img = document.createElement('img');
          img.src = pick.image;
          img.alt = '';
          img.loading = 'lazy';
          media.append(img);
        }
        link.hidden = false;
      });
    }
  }

  customElements.define('se-performance', SePerformance);
})();

/* Performance details drawer: on desktop it opens exactly over the card column, from the
   card's left edge to the screen edge, so it lines up with the section underneath. */
(() => {
  const fit = (section) => {
    const dialog = section?.querySelector('.se-perf-drawer');
    const card = section?.querySelector('.se-perf__card');
    if (!dialog || !card) return;
    const left = card.getBoundingClientRect().left;
    if (window.innerWidth >= 990 && left > window.innerWidth * 0.4) {
      dialog.style.setProperty('--se-drawer-w', `${Math.round(window.innerWidth - left)}px`);
    } else {
      dialog.style.removeProperty('--se-drawer-w');
    }
  };
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest?.('.se-perf__details-button');
      if (button) fit(button.closest('.shopify-section') || button.closest('section'));
    },
    true
  );
  window.addEventListener('resize', () => {
    document.querySelectorAll('.se-perf-drawer[open]').forEach((d) => fit(d.closest('.shopify-section')));
  });
})();
